import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../services/api';
import { toast } from 'react-toastify';
import PhotoEditorModal from '../components/common/PhotoEditorModal';
import ReportBlockMenu from '../components/social/ReportBlockMenu';
import { buildSocialUrl } from '../utils/socialLinks';

const AddPostModal = ({ vehicles, onClose }) => {
  const [caption, setCaption] = useState('');
  const [images, setImages] = useState([]);
  const [selectedCars, setSelectedCars] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleFiles = (e) => {
    const picked = Array.from(e.target.files).slice(0, 5);
    setImages(prev => [...prev, ...picked].slice(0, 5));
  };

  const removeImage = (i) => setImages(prev => prev.filter((_, idx) => idx !== i));

  const toggleCar = (id) => setSelectedCars(prev =>
    prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('content', caption);
      fd.append('postType', 'general');
      if (selectedCars.length > 0) fd.append('relatedVehicleId', selectedCars[0]);
      images.forEach(img => fd.append('images', img));
      await api.post('/feed/posts', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Post created!');
      onClose();
    } catch { toast.error('Failed to create post'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>New Post</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Caption</label>
              <textarea
                className="input"
                rows={3}
                placeholder="Write something..."
                value={caption}
                onChange={e => setCaption(e.target.value)}
              />
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Photos <span style={{ color: '#5a657a', fontWeight: 400 }}>(up to 20)</span></label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: '#1a2030', border: '1px solid #3a445c', cursor: 'pointer', fontSize: '0.85rem', color: '#9aa3b2' }}>
                Choose files
                <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFiles} />
              </label>
              {images.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  {images.map((img, i) => (
                    <div key={i} style={{ position: 'relative', width: 72, height: 72 }}>
                      <img src={URL.createObjectURL(img)} alt="" style={{ width: 72, height: 72, objectFit: 'cover', border: '1px solid #2a3142' }} />
                      <button type="button" onClick={() => removeImage(i)}
                        style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, background: 'rgba(0,0,0,0.7)', border: 'none', color: '#fff', fontSize: '0.7rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {vehicles.length > 0 && (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Tag Cars</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto', padding: '4px 0' }}>
                  {vehicles.map(uv => (
                    <label key={uv.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: selectedCars.includes(uv.id) ? 'rgba(201,206,214,0.08)' : '#141821', border: `1px solid ${selectedCars.includes(uv.id) ? '#c9ced6' : '#2a3142'}`, cursor: 'pointer', transition: 'all 0.15s' }}>
                      <input type="checkbox" checked={selectedCars.includes(uv.id)} onChange={() => toggleCar(uv.id)} style={{ accentColor: '#c9ced6' }} />
                      <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                        {uv.nickname || `${uv.vehicle?.year} ${uv.vehicle?.make} ${uv.vehicle?.model}`}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting || (!caption.trim() && images.length === 0)}>
              {submitting ? 'Posting...' : 'Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const HeartIcon = ({ filled, size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const CommentIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

// Stacked-frames mark for posts carrying more than one photo.
const MultiPhotoIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="8" y="3" width="13" height="13" rx="2" />
    <path d="M3 8v11a2 2 0 0 0 2 2h11" />
  </svg>
);

const PostModal = ({ post: initialPost, profile, me, onClose, onPostUpdate, onDelete }) => {
  const [post, setPost] = useState(initialPost);
  const [imgIndex, setImgIndex] = useState(0);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const commentInputRef = useRef();
  const commentsEndRef = useRef();

  const images = post.imageUrls || [];

  useEffect(() => {
    const loadComments = async () => {
      setCommentsLoading(true);
      try {
        const { data } = await api.get(`/feed/posts/${post.id}/comments`);
        setComments(data.comments || []);
      } catch { /* silent */ }
      finally { setCommentsLoading(false); }
    };
    loadComments();
  }, [post.id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (fullscreen) setFullscreen(false);
        else onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, fullscreen]);

  const toggleLike = async () => {
    try {
      if (post.liked) {
        await api.delete(`/feed/posts/${post.id}/like`);
        const updated = { ...post, liked: false, likeCount: (post.likeCount ?? 1) - 1 };
        setPost(updated);
        onPostUpdate?.(updated);
      } else {
        await api.post(`/feed/posts/${post.id}/like`);
        const updated = { ...post, liked: true, likeCount: (post.likeCount ?? 0) + 1 };
        setPost(updated);
        onPostUpdate?.(updated);
      }
    } catch { toast.error('Action failed'); }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/feed/posts/${post.id}/comments`, { content: commentText.trim() });
      setComments(prev => [...prev, data.comment]);
      const updated = { ...post, commentCount: (post.commentCount ?? 0) + 1 };
      setPost(updated);
      onPostUpdate?.(updated);
      setCommentText('');
      setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch { toast.error('Failed to post comment'); }
    finally { setSubmitting(false); }
  };

  const deletePost = async () => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try {
      await api.delete(`/feed/posts/${post.id}`);
      onDelete?.(post.id);
      onClose();
    } catch { toast.error('Failed to delete post'); }
  };

  const deleteComment = async (commentId) => {
    try {
      await api.delete(`/feed/posts/${post.id}/comments/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
      const updated = { ...post, commentCount: Math.max(0, (post.commentCount ?? 1) - 1) };
      setPost(updated);
      onPostUpdate?.(updated);
    } catch { toast.error('Failed to delete comment'); }
  };

  return (
    <div className="post-modal-overlay" onClick={onClose}>
      <div className="post-modal-wrap">
        <button className="event-detail-close-float" onClick={onClose}>✕</button>
        <div className="post-modal" onClick={e => e.stopPropagation()}>
        {/* Left: image */}
        <div className="post-modal-media">
          {images.length > 0 ? (
            <>
              <img src={images[imgIndex]} alt="" className="post-modal-img" onClick={() => setFullscreen(true)} />
              {images.length > 1 && (
                <>
                  {imgIndex > 0 && (
                    <button className="post-modal-arrow post-modal-arrow-left" onClick={() => setImgIndex(i => i - 1)}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                    </button>
                  )}
                  {imgIndex < images.length - 1 && (
                    <button className="post-modal-arrow post-modal-arrow-right" onClick={() => setImgIndex(i => i + 1)}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  )}
                  <div className="post-modal-dots">
                    {images.map((_, i) => <span key={i} className={i === imgIndex ? 'active' : ''} />)}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="post-modal-text-only">{post.content}</div>
          )}
        </div>

        {/* Right: sidebar */}
        <div className="post-modal-sidebar">
          <div className="post-modal-header">
            <Link to={`/profile/${profile.username}`} onClick={onClose} className="post-modal-author">
              {profile.profileImageUrl
                ? <img src={profile.profileImageUrl} alt="" className="post-modal-avatar" />
                : <div className="post-modal-avatar-ph">{profile.username?.[0]?.toUpperCase()}</div>}
              <span>{profile.username}</span>
            </Link>
            {me?.id !== profile?.id && profile?.id && (
              <ReportBlockMenu
                targetType="post"
                targetId={post.id}
                targetUserId={profile.id}
                targetLabel="post"
                onReported={() => { onDelete?.(post.id); onClose(); }}
                onBlocked={() => { onDelete?.(post.id); onClose(); }}
                triggerSize="small"
              />
            )}
            {me?.id === profile?.id && (
              <div style={{ position: 'relative', marginRight: 44, zIndex: 11 }}>
                <button
                  onClick={() => setMenuOpen(o => !o)}
                  style={{ background: 'none', border: 'none', color: '#8892b0', cursor: 'pointer', padding: '4px 8px', fontSize: '1.1rem', lineHeight: 1, borderRadius: 4 }}
                >
                  ···
                </button>
                {menuOpen && (
                  <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 1 }} onClick={() => setMenuOpen(false)} />
                    <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 2, background: '#1a2030', border: '1px solid #2a3142', borderRadius: 6, minWidth: 120, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
                      <button
                        onClick={() => { setMenuOpen(false); deletePost(); }}
                        style={{ display: 'block', width: '100%', padding: '10px 14px', background: 'none', border: 'none', color: '#e84545', cursor: 'pointer', fontSize: '0.85rem', textAlign: 'left', fontWeight: 500 }}
                      >
                        Delete post
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="post-modal-comments-area">
            {post.content && (
              <div className="post-modal-caption">
                <Link to={`/profile/${profile.username}`} onClick={onClose} className="post-modal-caption-user">{profile.username}</Link>
                {post.content}
              </div>
            )}
            {commentsLoading && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
                <div className="loading-spinner" style={{ width: 22, height: 22, borderWidth: 2 }} />
              </div>
            )}
            {!commentsLoading && comments.map(c => {
              const cAuthorId = c.author?.id || c.userId;
              const isOwnComment = me?.id === c.userId;
              const canDelete = me?.id === c.userId || me?.id === profile.id;
              return (
                <div key={c.id} className="post-modal-comment" style={{ alignItems: 'flex-start' }}>
                  <Link to={`/profile/${c.author?.username}`} onClick={onClose} className="post-modal-comment-avatar">
                    {c.author?.profileImageUrl
                      ? <img src={c.author.profileImageUrl} alt="" />
                      : <div className="post-modal-comment-avatar-ph">{c.author?.username?.[0]?.toUpperCase()}</div>}
                  </Link>
                  <div className="post-modal-comment-body" style={{ flex: 1, minWidth: 0 }}>
                    <Link to={`/profile/${c.author?.username}`} onClick={onClose} className="post-modal-comment-user">{c.author?.username}</Link>
                    <span className="post-modal-comment-text">{c.content}</span>
                  </div>
                  {canDelete && (
                    <button className="post-modal-comment-del" onClick={() => deleteComment(c.id)} aria-label="Delete">×</button>
                  )}
                  {!isOwnComment && (
                    <ReportBlockMenu
                      targetType="comment"
                      targetId={c.id}
                      targetUserId={cAuthorId}
                      targetLabel="comment"
                      onReported={() => setComments(prev => prev.filter(x => x.id !== c.id))}
                      onBlocked={(blockedUserId) => setComments(prev => prev.filter(x => (x.author?.id || x.userId) !== blockedUserId))}
                      triggerSize="small"
                    />
                  )}
                </div>
              );
            })}
            <div ref={commentsEndRef} />
          </div>

          <div className="post-modal-actions">
            <button className={`post-modal-like-btn${post.liked ? ' liked' : ''}`} onClick={toggleLike}>
              <HeartIcon filled={post.liked} />
            </button>
            <button className="post-modal-comment-trigger" onClick={() => commentInputRef.current?.focus()}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </button>
            <span className="post-modal-like-count">{post.likeCount ?? 0} {post.likeCount === 1 ? 'like' : 'likes'}</span>
          </div>

          <form className="post-modal-comment-form" onSubmit={submitComment}>
            <input
              ref={commentInputRef}
              type="text"
              placeholder="Add a comment…"
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              className="post-modal-comment-input"
              autoComplete="off"
            />
            <button type="submit" className="post-modal-comment-submit" disabled={submitting || !commentText.trim()}>
              Post
            </button>
          </form>
        </div>
      </div>

      </div>

      {fullscreen && (
        <div className="post-fullscreen" onClick={() => setFullscreen(false)}>
          {images.length > 1 && imgIndex > 0 && (
            <button className="post-fullscreen-arrow post-fullscreen-arrow-left" onClick={e => { e.stopPropagation(); setImgIndex(i => i - 1); }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          )}
          <img src={images[imgIndex]} alt="" className="post-fullscreen-img" onClick={e => e.stopPropagation()} />
          {images.length > 1 && imgIndex < images.length - 1 && (
            <button className="post-fullscreen-arrow post-fullscreen-arrow-right" onClick={e => { e.stopPropagation(); setImgIndex(i => i + 1); }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          )}
          <button className="post-fullscreen-close" onClick={() => setFullscreen(false)}>×</button>
        </div>
      )}
    </div>
  );
};

// ─── Shared helpers ───────────────────────────────────────────────────────────

const timeAgo = (date) => {
  const s = Math.floor((Date.now() - new Date(date)) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};



// Social URL building moved to utils/socialLinks → buildSocialUrl
// (accepts a bare username, @handle, or full URL).

// Clamp to Instagram bounds (4:5 portrait min, 1.91:1 landscape max) so a
// post with an extreme aspect can't blow out the card. Mirrors Feed.jsx +
// app/PostViewer.jsx so all three surfaces stay visually consistent.
const clampRatio = (w, h) => {
  if (!w || !h) return null;
  return Math.min(Math.max(w / h, 0.8), 1.91);
};

// One profile-feed list item. Owns its own aspect-ratio state so consecutive
// posts don't reflow each other when their images load. Seeds from
// post.imageMeta when present (no flicker); falls back to the natural size of
// the first image (one-frame layout shift on legacy posts only).
const ProfileFeedPost = ({ post, profile, imgIndex, setImgIndex, onOpenPost }) => {
  const images = post.imageUrls || [];
  const multi = images.length > 1;

  const seededRatio = clampRatio(post.imageMeta?.[0]?.w, post.imageMeta?.[0]?.h);
  const [aspectRatio, setAspectRatio] = useState(seededRatio ?? 1);
  const handleFirstImageLoad = (e) => {
    if (seededRatio) return;
    const { naturalWidth, naturalHeight } = e.currentTarget;
    const r = clampRatio(naturalWidth, naturalHeight);
    if (r) setAspectRatio(r);
  };

  return (
    <div id={`profile-post-${post.id}`} className="posts-feed-item">
      <div className="posts-feed-header">
        {profile.profileImageUrl
          ? <img src={profile.profileImageUrl} alt="" className="posts-feed-avatar" />
          : <div className="posts-feed-avatar-placeholder">{profile.username?.[0]?.toUpperCase()}</div>}
        <span className="posts-feed-username">{profile.username}</span>
        {post.createdAt && (
          <span className="posts-feed-date">
            {new Date(post.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        )}
      </div>

      <div
        className="posts-feed-images"
        style={{ cursor: 'pointer', aspectRatio: images.length > 0 ? aspectRatio : undefined }}
        onClick={() => onOpenPost(post)}
      >
        {images.length > 0
          ? <img
              src={post.thumbnailUrls?.[imgIndex] || images[imgIndex]}
              alt=""
              loading="lazy"
              decoding="async"
              onLoad={imgIndex === 0 ? handleFirstImageLoad : undefined}
            />
          : post.content
            ? <div className="posts-feed-images-empty">{post.content}</div>
            : null}
        {multi && imgIndex > 0 && (
          <button className="posts-feed-arrow posts-feed-arrow-left" onClick={e => { e.stopPropagation(); setImgIndex(imgIndex - 1); }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
        )}
        {multi && imgIndex < images.length - 1 && (
          <button className="posts-feed-arrow posts-feed-arrow-right" onClick={e => { e.stopPropagation(); setImgIndex(imgIndex + 1); }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        )}
      </div>

      {multi && (
        <div className="posts-feed-multi-dot">
          {images.map((_, i) => <span key={i} className={i === imgIndex ? 'active' : ''} />)}
        </div>
      )}

      <div className="posts-feed-actions">
        <button className="posts-feed-action-btn" onClick={() => onOpenPost(post)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill={post.liked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: post.liked ? '#e84545' : 'inherit' }}>
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
          <span>{post.likeCount ?? 0}</span>
        </button>
        <button className="posts-feed-action-btn" onClick={() => onOpenPost(post)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <span>{post.commentCount ?? 0}</span>
        </button>
      </div>

      {post.content && images.length > 0 && (
        <div className="posts-feed-caption">
          <strong>{profile.username}</strong>{post.content}
        </div>
      )}
    </div>
  );
};

const Profile = () => {
  const { username } = useParams();
  const { user: me } = useSelector(s => s.auth);
  const [profile, setProfile] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followModal, setFollowModal] = useState(null); // 'followers' | 'following' | null
  const [followList, setFollowList] = useState([]);
  const [followLoading, setFollowLoading] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsView, setPostsView] = useState('grid');
  const [openPost, setOpenPost] = useState(null);
  const [feedImgIndex, setFeedImgIndex] = useState({});
  const [scrollToPostId, setScrollToPostId] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/users/${username}/profile`);
        setProfile(data.user);
        const [statsRes, vehiclesRes] = await Promise.all([
          api.get(`/users/${data.user.id}/stats`),
          api.get(`/garages/${data.user.id}/vehicles`)
        ]);
        setStats(statsRes.data);
        setVehicles(vehiclesRes.data.vehicles);
      } catch (e) { toast.error('Profile not found'); }
      finally { setLoading(false); }
    };
    load();
  }, [username]);

  const fetchPosts = useCallback(async () => {
    if (!profile) return;
    setPostsLoading(true);
    try {
      const { data } = await api.get(`/feed/users/${profile.id}/posts`);
      setPosts(data.posts || []);
    } catch { /* silently fail */ }
    finally { setPostsLoading(false); }
  }, [profile]);

  useEffect(() => {
    if (profile) fetchPosts();
  }, [profile, fetchPosts]);

  // When a grid post is clicked, switch to list view and scroll to that post.
  useEffect(() => {
    if (!scrollToPostId || postsView !== 'list' || postsLoading) return;
    // wait one paint so the list view has rendered
    const id = requestAnimationFrame(() => {
      const el = document.getElementById(`profile-post-${scrollToPostId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setScrollToPostId(null);
    });
    return () => cancelAnimationFrame(id);
  }, [scrollToPostId, postsView, postsLoading, posts]);

  const openFollowModal = useCallback(async (type) => {
    setFollowModal(type);
    setFollowList([]);
    setFollowLoading(true);
    try {
      const { data } = await api.get(`/users/${profile.id}/${type}`);
      setFollowList(data.users || data.followers || data.following || []);
    } catch { toast.error('Could not load list'); }
    finally { setFollowLoading(false); }
  }, [profile]);

  const toggleFollow = async () => {
    if (!profile) return;
    try {
      if (profile.isFollowing || profile.followRequestPending) {
        const { data } = await api.delete(`/users/${profile.id}/follow`);
        setProfile(p => ({ ...p, isFollowing: false, followRequestPending: false, followerCount: p.isFollowing ? p.followerCount - 1 : p.followerCount }));
      } else {
        const { data } = await api.post(`/users/${profile.id}/follow`);
        setProfile(p => ({ ...p, isFollowing: data.following, followRequestPending: data.pending, followerCount: data.following ? p.followerCount + 1 : p.followerCount }));
      }
    } catch { toast.error('Action failed'); }
  };

  if (loading) return <div className="loading-page"><div className="loading-spinner" /></div>;
  if (!profile) return <div className="container page"><div className="empty-state"><h3>User not found</h3></div></div>;

  // Compare identities, not strings. This used to be `me?.username === username`,
  // which measured the wrong two things: the redux handle against the raw URL
  // param. The server resolves that param with lower(trim(username)), so
  // /profile/ROOTY served your own row while this said it wasn't yours — you
  // got Follow and Report buttons on your own profile, and if your account is
  // private, the gate below hid your own posts from you (you don't follow
  // yourself). Renaming from another device broke it the same way, since both
  // strings can be stale while the ids never are.
  //
  // `profile` is the row the server actually resolved and it always carries an
  // id (the !profile guard above has already returned), so this is the same
  // question every ownership check on the server asks.
  const isMe = !!me?.id && me.id === profile.id;
  const socialLinks = [
    { label: 'Instagram', url: buildSocialUrl('instagram', profile.instagramUrl) },
    { label: 'TikTok', url: buildSocialUrl('tiktok', profile.tiktokUrl) },
    { label: 'Facebook', url: buildSocialUrl('facebook', profile.facebookUrl) },
  ].filter(l => l.url);

  const displayName = (profile.firstName || profile.lastName)
    ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim()
    : profile.username;

  return (
    <div className="profile-page">
      {/* Header — edge-to-edge, matches garage page header */}
      <div className="profile-page-header">
        <div className="profile-page-left">
          {profile.profileImageUrl
            ? <img src={profile.profileImageUrl} alt="" className="profile-page-avatar" />
            : <div className="profile-page-avatar-placeholder">{profile.username?.[0]?.toUpperCase()}</div>}
        </div>
        <div className="profile-page-title-block">
          <h1 className="profile-page-title">{displayName}</h1>
          <p className="profile-page-meta">
            <span>@{profile.username}</span>
            <span className="profile-meta-sep">·</span>
            <button className="profile-meta-link" onClick={() => openFollowModal('followers')}>
              {profile.followerCount ?? 0} Followers
            </button>
            <span className="profile-meta-sep">·</span>
            <button className="profile-meta-link" onClick={() => openFollowModal('following')}>
              {profile.followingCount ?? 0} Following
            </button>
            {profile.state && (
              <>
                <span className="profile-meta-sep">·</span>
                <span>{profile.state}</span>
              </>
            )}
          </p>
        </div>
        <div className="profile-page-actions">
          <Link to={`/garage/${profile.id}`} className="btn-primary btn-sm" style={{ textDecoration: 'none' }}>
            View Collection
          </Link>
          {isMe
            ? <>
                <Link to="/settings" className="btn-secondary btn-sm">Settings</Link>
              </>
            : <button
                className={profile.isFollowing ? 'btn-secondary btn-sm' : 'btn-secondary btn-sm'}
                onClick={toggleFollow}
                style={profile.followRequestPending ? { opacity: 0.7 } : {}}
              >
                {profile.isFollowing ? 'Following' : profile.followRequestPending ? 'Requested' : 'Follow'}
              </button>}
          {socialLinks.map(l => (
            <a key={l.label} href={l.url} target="_blank" rel="noreferrer noopener"
              className="btn-primary btn-sm" style={{ textDecoration: 'none' }}>
              {l.label}
            </a>
          ))}
          {!isMe && profile?.id && (
            <ReportBlockMenu
              targetType="user"
              targetId={profile.id}
              targetUserId={profile.id}
              targetLabel="profile"
              onBlocked={() => { window.location.assign('/'); }}
            />
          )}
        </div>
      </div>

      <div className="container">

      {/* Private profile gate */}
      {!isMe && profile.isPrivate && !profile.isFollowing ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14, color: '#9aa3b2' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <div style={{ fontWeight: 600, fontSize: '1rem', marginBottom: 8 }}>This account is private</div>
          <div style={{ color: '#5a657a', fontSize: '0.875rem' }}>
            {profile.followRequestPending
              ? 'Your follow request is pending approval.'
              : 'Follow this account to see their posts and garage.'}
          </div>
        </div>
      ) : <>

      {/* Posts */}
      <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, marginTop: 24 }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 600, color: '#9aa3b2', margin: 0 }}>Posts</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div className="posts-view-toggle">
                <button
                  className={`posts-view-btn${postsView === 'grid' ? ' active' : ''}`}
                  onClick={() => setPostsView('grid')}
                  aria-label="Grid view"
                >
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="currentColor">
                    <rect x="0" y="0" width="6.5" height="6.5" />
                    <rect x="8.5" y="0" width="6.5" height="6.5" />
                    <rect x="0" y="8.5" width="6.5" height="6.5" />
                    <rect x="8.5" y="8.5" width="6.5" height="6.5" />
                  </svg>
                </button>
                <button
                  className={`posts-view-btn${postsView === 'list' ? ' active' : ''}`}
                  onClick={() => setPostsView('list')}
                  aria-label="List view"
                >
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="currentColor">
                    <rect x="0" y="1" width="15" height="2" />
                    <rect x="0" y="6.5" width="15" height="2" />
                    <rect x="0" y="12" width="15" height="2" />
                  </svg>
                </button>
              </div>
              {isMe && <button className="btn-primary btn-sm" onClick={() => setShowPostModal(true)}>Add Post</button>}
            </div>
          </div>
          {postsLoading && <div className="loading-page" style={{ minHeight: 200 }}><div className="loading-spinner" /></div>}
          {!postsLoading && posts.length === 0 && (
            <div className="empty-state" style={{ paddingTop: 32 }}>
              <h3>No posts yet</h3>
              <p style={{ color: '#5a657a', fontSize: '0.875rem' }}>Posts will appear here.</p>
            </div>
          )}
          {!postsLoading && posts.length > 0 && postsView === 'grid' && (
            <div className="posts-grid">
              {posts.map(post => {
                const thumb = post.thumbnailUrls?.[0] || post.imageUrls?.[0];
                const multi = post.imageUrls?.length > 1;
                return (
                  <div key={post.id} className="posts-grid-item" onClick={() => {
                    setScrollToPostId(post.id);
                    setPostsView('list');
                  }}>
                    {thumb
                      ? <img src={thumb} alt="" className="posts-grid-img" loading="lazy" decoding="async" />
                      : <div className="posts-grid-text">{post.content}</div>}
                    {multi && (
                      <span className="posts-grid-multi" aria-label="Multiple photos">
                        <MultiPhotoIcon />
                      </span>
                    )}
                    <div className="posts-grid-overlay">
                      <span><HeartIcon filled size={16} /> {post.likeCount ?? 0}</span>
                      <span><CommentIcon size={16} /> {post.commentCount ?? 0}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {!postsLoading && posts.length > 0 && postsView === 'list' && (
            <div className="posts-feed">
              {posts.map(post => (
                <ProfileFeedPost
                  key={post.id}
                  post={post}
                  profile={profile}
                  imgIndex={feedImgIndex[post.id] ?? 0}
                  setImgIndex={(i) => setFeedImgIndex(s => ({ ...s, [post.id]: i }))}
                  onOpenPost={async (p) => {
                    try { const { data } = await api.get(`/feed/posts/${p.id}`); setOpenPost(data.post); }
                    catch { setOpenPost(p); }
                  }}
                />
              ))}
            </div>
          )}
      </>

      </>}
      </div>
      {showPostModal && (
        <PhotoEditorModal
          vehicles={vehicles.filter(v => !v.isDreamBuild)}
          onClose={() => setShowPostModal(false)}
          onCreated={fetchPosts}
        />
      )}

      {openPost && (
        <PostModal
          post={openPost}
          profile={profile}
          me={me}
          onClose={() => setOpenPost(null)}
          onPostUpdate={(updated) => {
            setPosts(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
            setOpenPost(updated);
          }}
          onDelete={(id) => setPosts(prev => prev.filter(p => p.id !== id))}
        />
      )}

      {followModal && (
        <div className="modal-overlay" onClick={() => setFollowModal(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>
                {followModal === 'followers' ? 'Followers' : 'Following'}
              </h3>
              <button className="close-btn" onClick={() => setFollowModal(null)}>×</button>
            </div>
            <div className="modal-body" style={{ padding: 0, maxHeight: 480, overflowY: 'auto' }}>
              {followLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                  <div className="loading-spinner" style={{ width: 28, height: 28, borderWidth: 2 }} />
                </div>
              )}
              {!followLoading && followList.length === 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: '#5a657a' }}>No {followModal} yet</div>
              )}
              {!followLoading && followList.map(u => (
                <Link
                  key={u.id}
                  to={`/profile/${u.username}`}
                  onClick={() => setFollowModal(null)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderBottom: '1px solid #2a3142', color: '#f0f1f5', textDecoration: 'none' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#1a2030'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  {u.profileImageUrl
                    ? <img src={u.profileImageUrl} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: '1px solid #3a445c', flexShrink: 0 }} />
                    : <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#1a2030', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#c9ced6', border: '1px solid #3a445c', flexShrink: 0 }}>{u.username?.[0]?.toUpperCase()}</div>}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{u.firstName || u.username} {u.lastName || ''}</div>
                    <div style={{ color: '#5a657a', fontSize: '0.8rem' }}>@{u.username}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default Profile;
